import React, { useMemo, useState } from 'react';
import { Checkbox, Input, Popover } from 'antd';
import './multi-select.scss';
import CustomIcon, { IconType } from '../custom-icon/custom-icon';
import Chevron from '@svg/chevron-down_20.svg?react';

interface MultiSelectProps {
    showSearch?: boolean;
    showAllOption?: boolean;
    options: { label: string; value: string }[];
    selectedList: string[];
    onSelectionChange: (selectedList: string[]) => void;
    overlayClassName?: string;
    className?: string;
    disabled?: boolean;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
                                                            showSearch = false,
                                                            showAllOption = true,
                                                            options,
                                                            selectedList,
                                                            onSelectionChange,
                                                            overlayClassName,
                                                            className,
                                                            disabled
                                                        }) => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [isAllSelected, setIsAllSelected] = useState(false);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    const handleCheckboxChange = (itemValue: string) => {
        if (isAllSelected || selectedList.includes(itemValue)) {
            uncheckItem(itemValue);
        } else {
            checkItem(itemValue);
        }
    };

    const handleAllCheckboxChange = () => {
        if (isAllSelected) {
            setIsAllSelected(false);
        }
        else {
            setIsAllSelected(true);
            onSelectionChange([]);
        }
    };

    const checkItem = (value: string) => {
        const updatedList = [...selectedList, value];
        if (updatedList.length === options.length) {
            setIsAllSelected(true);
            onSelectionChange([]);
        }
        else {
            setIsAllSelected(false);
            onSelectionChange(updatedList);
        }
    };

    const uncheckItem = (value: string) => {
        if (isAllSelected) {
            setIsAllSelected(false);
            onSelectionChange(options.map((item) => item.value).filter(val => val !== value));
        }
        else {
            onSelectionChange(selectedList.filter((val) => val !== value));
        }
    };

    const filteredItems = useMemo(() => {
        return options.filter((item) =>
            item.label.toLowerCase().includes(searchValue.toLowerCase())
        );
    }, [options, searchValue]);

    const content = (
        <div className={'multi-select-box-wrapper'}>
            {showSearch && (
                <div className={'search-wrapper'}>
                    <Input
                        prefix={<CustomIcon type={IconType.SEARCH} size={20}/>}
                        className="search-input"
                        placeholder="Search"
                        value={searchValue}
                        onChange={handleSearchChange}
                    />
                </div>
            )}
            <div className={'multi-select-option-list'}>
                {showAllOption && filteredItems.length === options.length && (
                    <Checkbox className={'multi-select-option'}
                              checked={isAllSelected}
                              onChange={handleAllCheckboxChange}>
                        All
                    </Checkbox>
                )}
                {filteredItems.map((item) => (
                    <Checkbox className={'multi-select-option'} key={item.value}
                              checked={isAllSelected || selectedList.includes(item.value)}
                              onChange={() => handleCheckboxChange(item.value)}>
                        {item.label}
                    </Checkbox>
                ))}
            </div>
        </div>
    );

    const label = useMemo(() => {
        if (isAllSelected) {
            return 'All';
        } else if (selectedList.length === 0) {
            return 'Select';
        } else if (selectedList.length === 1) {
            return options.find((item) => item.value === selectedList[0])?.label;
        } else {
            return `${selectedList.length} items selected`;
        }
    }, [selectedList, options, isAllSelected]);

    return (
        <Popover content={content}
            overlayClassName={`multi-select-overlay ${overlayClassName}`}
            trigger="click"
            placement={'bottomLeft'}
            showArrow={false}>
            <div className={`multi-select-label ${className} ${disabled ? 'disabled' : ''}`}>
                <div className="label" >{label}</div><Chevron /></div>
        </Popover>
    );
};

export default MultiSelect;